import {
  poolUtils,
  V1PoolInfo,
  PoolReserves,
  SignerTransaction,
  ContractVersionValue,
  V2PoolInfo
} from "@tinymanorg/tinyman-js-sdk";

import {WalletType} from "../../../wallet/util/walletTypes";
import {algod} from "../algosdk/AlgodManager";
import peraWalletManager from "../pera-wallet/PeraWalletManager";
import deflyWalletManager from "../defly/DeflyWalletManager";
import {PREFERRED_NETWORK} from "../../constants";
import walletConnectManager from "../wallet-connect/WalletConnect.manager";

async function getPoolInfoWithReserveDetail({
  asset1ID,
  asset2ID,
  contractVersion
}: {
  asset1ID: number;
  asset2ID: number;
  contractVersion: ContractVersionValue;
}) {
  const poolInfo = await poolUtils[contractVersion].getPoolInfo({
    client: algod.client,
    asset1ID,
    asset2ID,
    network: PREFERRED_NETWORK
  });
  const reserves = await retrievePoolReserves(contractVersion, poolInfo);

  return {
    poolInfo,
    reserves
  };
}

async function retrievePoolReserves<ContractVersion extends ContractVersionValue>(
  contractVersion: ContractVersion,
  poolInfo: ContractVersion extends "v2" ? V2PoolInfo : V1PoolInfo
) {
  let reserves: null | PoolReserves = null;

  if (poolUtils[contractVersion].isPoolReady(poolInfo)) {
    reserves = await poolUtils[contractVersion].getPoolReserves(algod.client, poolInfo);
  }

  return reserves;
}

interface InitiateSignCeremonyOptions {
  walletType: WalletType;
  signerAddress: null | string;
}

/**
 * Delegates `txn` to the appropriate wallet's signing method
 */
function initiateSignCeremony(
  {walletType, signerAddress}: InitiateSignCeremonyOptions,
  txGroups: SignerTransaction[][]
): Promise<Uint8Array[]> {
  switch (walletType) {
    case WalletType.PeraWallet: {
      return peraWalletManager.signTransaction(txGroups, signerAddress ?? undefined);
    }

    case WalletType.WalletConnect: {
      return walletConnectManager.signTransaction(txGroups, signerAddress ?? undefined);
    }

    case WalletType.DeflyWallet: {
      return deflyWalletManager.signTransaction(txGroups, signerAddress ?? undefined);
    }

    default: {
      return Promise.reject(
        new Error(
          "You need to connect to a wallet before attempting to sign a transaction"
        )
      );
    }
  }
}

export type ConfirmedTxnProcessData = {
  confirmedRound: number;
  txnID: string;
  signedTxns: Uint8Array[];
}[];

export {getPoolInfoWithReserveDetail, retrievePoolReserves, initiateSignCeremony};
